import {
  ArrowLeftIcon,
  DotsThreeIcon,
  EyeSlashIcon,
  PencilSimpleIcon,
  PlusIcon,
  XIcon,
} from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { resolveWorkflowTemplateArtwork } from './workflow-library/WorkflowTemplateArtwork';
import { WorkflowTemplatePreview } from './workflow-library/WorkflowTemplatePreview';
import { WorkflowTemplateRow } from './workflow-library/WorkflowTemplateRow';

import { NewWorkflowDialog } from '@/components/plans/workflow-library/NewWorkflowDialog';
import { sortFeaturedWorkflows } from '@/components/plans/workflow-library/workflow-utils';
import { Button } from '@/components/ui/button';
import { CircleIconButton } from '@/components/ui/circle-icon-button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { Skeleton } from '@/components/ui/skeleton';
import { StarterCard } from '@/components/ui/starter-card';
import { queryKeys } from '@/config/queryClient';
import { useIsMobile } from '@/hooks/use-mobile';
import { isCustomWorkflowsEnabled } from '@/lib/feature-flags';
import { campaignService } from '@/services/data/CampaignService';
import type { WorkflowTemplate } from '@/services/data/CampaignService';

interface WorkflowLibraryDialogProps {
  /**
   * Project to assign the workflow to. Optional — when omitted the dialog is
   * in discovery mode and the parent must supply `onAssign` to handle the
   * missing-project case (e.g. lazy-create a project from the chat).
   */
  projectId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Optional handler invoked when the user clicks "Use this". When provided,
   * replaces the default `campaignService.assignWorkflow(projectId, templateId)`.
   */
  onAssign?: (templateId: string) => void | Promise<void>;
  /**
   * P3.1 — when provided, the preview pane shows a secondary "Attach to next
   * message" CTA that calls this handler instead of starting the workflow
   * durably. Mike-style per-message chip pattern.
   */
  onAttach?: (template: WorkflowTemplate) => void;
  /**
   * G1 — when provided, restrict the templates list to those whose
   * `metadata.input_kind` matches. Used by Discover to filter to article-eligible
   * workflows.
   */
  inputKindFilter?: 'article' | 'photos' | 'url' | 'manual';
  /**
   * G1 — override the dialog title (e.g. "Pick a workflow" instead of the
   * default "Workflow library").
   */
  titleOverride?: string;
  /**
   * G1 — override the dialog description (defaults to sr-only generic copy).
   * Rendered visibly when provided.
   */
  descriptionOverride?: string;
  /**
   * G1 — fallback handler invoked from the empty-state button when an
   * `inputKindFilter` matches no templates. Lets the parent fall through to
   * a different create path (e.g. open article in chat without workflow).
   */
  onEmptyFallback?: () => void;
  /** Primary commit label. Contextual discovery surfaces can use "Start project". */
  useLabel?: string;
  /** Projects discovery uses the Home-style illustrated catalog instead of a split list. */
  cardLayout?: boolean;
  /** Initial catalog section for a deep link from the retired workflows route. */
  initialCardView?: 'all' | 'mine' | 'hidden';
}

function dispatchOpenSettings(section: string): void {
  window.dispatchEvent(new CustomEvent('mora:openSettings', { detail: section }));
}

export const WorkflowLibraryDialog: React.FC<WorkflowLibraryDialogProps> = ({
  projectId,
  open,
  onOpenChange,
  onAssign,
  onAttach,
  inputKindFilter,
  titleOverride,
  descriptionOverride,
  onEmptyFallback,
  useLabel,
  cardLayout = false,
  initialCardView = 'all',
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  const {
    data: templates = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: inputKindFilter
      ? queryKeys.workflows.inputKind(inputKindFilter)
      : queryKeys.workflows.templates(),
    queryFn: async () => {
      const response = inputKindFilter
        ? await campaignService.listWorkflowTemplatesByInputKind(inputKindFilter)
        : await campaignService.listWorkflowTemplates();
      if (response.error) throw new Error(response.error.message);
      return response.data ?? [];
    },
    enabled: open,
  });

  // eng-review H7 — re-assignment confirm. When `assignWorkflow` rejects with
  // `workflow_already_active`, surface a confirm dialog before retrying with
  // `{ force: true }`. Only relevant when we own the assignment (no onAssign).
  const [reassignTemplateId, setReassignTemplateId] = React.useState<string | null>(null);

  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [mobileView, setMobileView] = React.useState<'list' | 'preview'>('list');
  const [cardView, setCardView] = React.useState<'all' | 'mine' | 'hidden'>(initialCardView);
  const [showAllCards, setShowAllCards] = React.useState(false);
  const [newWorkflowOpen, setNewWorkflowOpen] = React.useState(false);
  const [editTemplate, setEditTemplate] = React.useState<WorkflowTemplate | null>(null);
  const lastSelectedIdRef = React.useRef<string | null>(null);
  const backButtonRef = React.useRef<HTMLButtonElement>(null);
  const catalogMenuButtonRef = React.useRef<HTMLButtonElement>(null);
  const dialogContentRef = React.useRef<HTMLDivElement>(null);
  const cardRefs = React.useRef(new Map<string, HTMLButtonElement>());
  const customWorkflowsEnabled = isCustomWorkflowsEnabled();

  const hiddenTemplatesQ = useQuery({
    queryKey: queryKeys.workflows.hidden(),
    queryFn: async () => {
      const response = await campaignService.listHiddenWorkflowTemplates();
      if (response.error) throw new Error(response.error.message);
      return response.data ?? [];
    },
    enabled: open && cardLayout && cardView === 'hidden',
  });

  // No search — the library is a short curated list (a handful of workflows),
  // and a search bar over ~5 rows is chrome, not utility (Subtraction).
  const filteredTemplates = templates;

  const selectedTemplate = React.useMemo(
    () => templates.find((t) => t.id === selectedId) ?? null,
    [templates, selectedId],
  );

  React.useEffect(() => {
    if (!open) {
      setSelectedId(null);
      setMobileView('list');
      setCardView(initialCardView);
      setShowAllCards(false);
    }
  }, [initialCardView, open]);

  React.useEffect(() => {
    setShowAllCards(false);
  }, [cardView]);

  React.useEffect(() => {
    if (cardLayout && selectedTemplate) backButtonRef.current?.focus();
  }, [cardLayout, selectedTemplate]);

  const assignMutation = useMutation({
    mutationFn: async (args: { templateId: string; force?: boolean }) => {
      if (onAssign) {
        await onAssign(args.templateId);
        return;
      }
      if (!projectId) {
        throw new Error('WorkflowLibraryDialog: projectId or onAssign required');
      }
      const resp = await campaignService.assignWorkflow(projectId, args.templateId, {
        force: args.force,
      });
      if (resp.error) {
        throw resp.error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      onOpenChange(false);
      if (!onAssign && projectId) {
        navigate(`/?planId=${projectId}&workflow=start`);
      }
    },
    onError: (err: unknown) => {
      if ((err as { handled?: boolean } | null)?.handled) return;
      const code = (err as { code?: string } | null)?.code;
      const message = err instanceof Error ? err.message : 'Could not assign workflow.';
      if (code === 'workflow_already_active') {
        // Stash the templateId and surface confirm dialog instead of toasting.
        // The user explicitly opts in to replace the running workflow.
        return;
      }
      toast.error(message);
    },
  });

  const handleAssignClick = (templateId: string): void => {
    if (!onAssign && projectId) {
      // Pre-flight: when the dialog owns the assignment, attempt without force.
      // The mutation's onError will short-circuit on workflow_already_active so
      // we can prompt; everything else surfaces via toast.
      assignMutation.mutate(
        { templateId },
        {
          onError: (err) => {
            const code = (err as { code?: string } | null)?.code;
            if (code === 'workflow_already_active') {
              setReassignTemplateId(templateId);
            }
          },
        },
      );
      return;
    }
    assignMutation.mutate({ templateId });
  };

  const confirmReassign = (): void => {
    if (!reassignTemplateId) return;
    const id = reassignTemplateId;
    setReassignTemplateId(null);
    assignMutation.mutate({ templateId: id, force: true });
  };

  const handleSelect = (id: string): void => {
    setSelectedId(id);
    if (isMobile) setMobileView('preview');
  };

  const showLeftPane = !isMobile || mobileView === 'list';
  const showRightPane = !isMobile || mobileView === 'preview';

  const invalidateTemplateLists = (): void => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.workflows.templates() });
    void queryClient.invalidateQueries({ queryKey: queryKeys.workflows.hidden() });
  };

  const focusAfterCardRemoval = (remainingIds: string[], removedId: string): void => {
    const removedIndex = remainingIds.indexOf(removedId);
    const nextId = remainingIds[removedIndex + 1] ?? remainingIds[removedIndex - 1] ?? null;

    requestAnimationFrame(() => {
      if (nextId && cardRefs.current.get(nextId)) {
        cardRefs.current.get(nextId)?.focus();
        return;
      }
      catalogMenuButtonRef.current?.focus();
    });
  };

  const hideTemplate = async (template: WorkflowTemplate): Promise<void> => {
    const response = await campaignService.hideWorkflowTemplate(template.id);
    if (response.error) {
      toast.error(`Couldn't hide this workflow: ${response.error.message}`);
      return;
    }
    setSelectedId(null);
    focusAfterCardRemoval(
      filteredTemplates.map((item) => item.id),
      template.id,
    );
    invalidateTemplateLists();
    toast.success(`Hid "${template.name}"`, {
      action: {
        label: 'Undo',
        onClick: () =>
          void campaignService.unhideWorkflowTemplate(template.id).then(invalidateTemplateLists),
      },
    });
  };

  const unhideTemplate = async (template: WorkflowTemplate): Promise<void> => {
    const response = await campaignService.unhideWorkflowTemplate(template.id);
    if (response.error) {
      toast.error(`Couldn't restore this workflow: ${response.error.message}`);
      return;
    }
    focusAfterCardRemoval(
      (hiddenTemplatesQ.data ?? []).map((item) => item.id),
      template.id,
    );
    invalidateTemplateLists();
    toast.success(`Restored "${template.name}"`);
  };

  const openCardPreview = (templateId: string): void => {
    lastSelectedIdRef.current = templateId;
    setSelectedId(templateId);
  };

  const returnToCards = (): void => {
    const previousId = lastSelectedIdRef.current;
    setSelectedId(null);
    requestAnimationFrame(() => {
      if (previousId) cardRefs.current.get(previousId)?.focus();
    });
  };

  if (cardLayout) {
    const cardTemplates =
      cardView === 'hidden'
        ? (hiddenTemplatesQ.data ?? [])
        : cardView === 'mine'
          ? filteredTemplates.filter((template) => Boolean(template.user_id))
          : filteredTemplates;
    const orderedCardTemplates = sortFeaturedWorkflows(cardTemplates);
    const cardLoading = cardView === 'hidden' ? hiddenTemplatesQ.isLoading : isLoading;
    const cardError = cardView === 'hidden' ? hiddenTemplatesQ.isError : isError;
    const retryCards = cardView === 'hidden' ? hiddenTemplatesQ.refetch : refetch;

    return (
      <>
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent
            ref={dialogContentRef}
            size="full"
            hideCloseButton
            aria-describedby="workflow-catalog-description"
            tabIndex={-1}
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              dialogContentRef.current?.focus({ preventScroll: true });
            }}
            className="flex h-[82vh] max-h-[760px] flex-col gap-0 overflow-hidden p-0 sm:max-w-5xl"
          >
            {selectedTemplate ? (
              <>
                <DialogHeader className="sr-only">
                  <DialogTitle>{selectedTemplate.name}</DialogTitle>
                  <DialogDescription id="workflow-catalog-description">
                    Review this workflow before starting a project.
                  </DialogDescription>
                </DialogHeader>
                <div className="flex items-center justify-between gap-4 px-5 py-3">
                  <button
                    ref={backButtonRef}
                    type="button"
                    onClick={returnToCards}
                    className="inline-flex items-center gap-1.5 text-detail text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <ArrowLeftIcon className="size-3" weight="regular" />
                    All workflows
                  </button>
                  <div className="flex shrink-0 items-center gap-1">
                    {selectedTemplate.user_id && customWorkflowsEnabled && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <CircleIconButton size="sm" ariaLabel="Manage this workflow">
                            <DotsThreeIcon weight="bold" />
                          </CircleIconButton>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => setEditTemplate(selectedTemplate)}>
                            <PencilSimpleIcon className="size-4" />
                            Edit workflow
                          </DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => void hideTemplate(selectedTemplate)}>
                            <EyeSlashIcon className="size-4" />
                            Hide workflow
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    <CircleIconButton
                      size="sm"
                      ariaLabel="Close"
                      onClick={() => onOpenChange(false)}
                    >
                      <XIcon weight="regular" />
                    </CircleIconButton>
                  </div>
                </div>
                <div className="min-h-0 flex-1">
                  <WorkflowTemplatePreview
                    template={selectedTemplate}
                    onUse={handleAssignClick}
                    isAssigning={assignMutation.isPending}
                    useLabel={useLabel}
                    secondaryLabel={
                      selectedTemplate.user_id && customWorkflowsEnabled
                        ? undefined
                        : 'Hide workflow'
                    }
                    onSecondary={
                      selectedTemplate.user_id && customWorkflowsEnabled
                        ? undefined
                        : () => void hideTemplate(selectedTemplate)
                    }
                    onAttach={
                      onAttach
                        ? (id) => {
                            const template = templates.find((item) => item.id === id);
                            if (template) {
                              onAttach(template);
                              onOpenChange(false);
                            }
                          }
                        : undefined
                    }
                  />
                </div>
              </>
            ) : (
              <>
                <DialogHeader className="px-4 py-4 text-left sm:px-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <DialogTitle>
                        {cardView === 'hidden'
                          ? 'Hidden workflows'
                          : cardView === 'mine'
                            ? 'Your workflows'
                            : (titleOverride ?? 'All workflows')}
                      </DialogTitle>
                      <DialogDescription
                        id="workflow-catalog-description"
                        className="mt-1 text-detail text-muted-foreground"
                      >
                        {cardView === 'hidden'
                          ? 'Restore workflows you want to use again.'
                          : cardView === 'mine'
                            ? 'Workflows you created for your team.'
                            : (descriptionOverride ?? 'Pick a workflow to start a guided project.')}
                      </DialogDescription>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <CircleIconButton
                            ref={catalogMenuButtonRef}
                            size="sm"
                            ariaLabel="Manage workflows"
                          >
                            <DotsThreeIcon weight="bold" />
                          </CircleIconButton>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {cardView !== 'all' && (
                            <DropdownMenuItem onSelect={() => setCardView('all')}>
                              <ArrowLeftIcon className="size-4" />
                              All workflows
                            </DropdownMenuItem>
                          )}
                          {cardView !== 'mine' && (
                            <DropdownMenuItem onSelect={() => setCardView('mine')}>
                              <PencilSimpleIcon className="size-4" />
                              Your workflows
                            </DropdownMenuItem>
                          )}
                          {cardView !== 'hidden' && (
                            <DropdownMenuItem onSelect={() => setCardView('hidden')}>
                              <EyeSlashIcon className="size-4" />
                              Hidden workflows
                            </DropdownMenuItem>
                          )}
                          {customWorkflowsEnabled && (
                            <DropdownMenuItem onSelect={() => setNewWorkflowOpen(true)}>
                              <PlusIcon className="size-4" />
                              New workflow
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <CircleIconButton
                        size="sm"
                        ariaLabel="Close"
                        onClick={() => onOpenChange(false)}
                      >
                        <XIcon weight="regular" />
                      </CircleIconButton>
                    </div>
                  </div>
                </DialogHeader>
                <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
                  {cardLoading && (
                    <div
                      className="grid grid-cols-1 gap-3 sm:grid-cols-2"
                      role="status"
                      aria-label="Loading workflows"
                    >
                      {Array.from({ length: 6 }).map((_, index) => (
                        <Skeleton key={index} className="h-[152px] rounded-xl" />
                      ))}
                    </div>
                  )}

                  {cardError && (
                    <Empty className="h-full border-0">
                      <EmptyHeader>
                        <EmptyTitle>Couldn&apos;t load workflows</EmptyTitle>
                        <EmptyDescription>Try loading the workflow catalog again.</EmptyDescription>
                      </EmptyHeader>
                      <Button variant="flat" size="sm" onClick={() => void retryCards()}>
                        Try again
                      </Button>
                    </Empty>
                  )}

                  {!cardLoading && !cardError && cardTemplates.length === 0 && (
                    <Empty className="h-full border-0">
                      <EmptyHeader>
                        <EmptyTitle>
                          {cardView === 'hidden'
                            ? 'Nothing hidden'
                            : cardView === 'mine'
                              ? 'No workflows of your own'
                              : 'No visible workflows'}
                        </EmptyTitle>
                        <EmptyDescription>
                          {cardView === 'hidden'
                            ? 'Workflows you hide will appear here.'
                            : cardView === 'mine'
                              ? 'Create a workflow to add it to your team library.'
                              : 'Review hidden workflows to restore a starter.'}
                        </EmptyDescription>
                      </EmptyHeader>
                      {cardView !== 'hidden' && (
                        <Button variant="flat" size="sm" onClick={() => setCardView('hidden')}>
                          Review hidden workflows
                        </Button>
                      )}
                    </Empty>
                  )}

                  {!cardLoading && !cardError && orderedCardTemplates.length > 0 && (
                    <>
                      <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {orderedCardTemplates
                          .slice(0, showAllCards ? undefined : 6)
                          .map((template) => {
                            const Illustration = resolveWorkflowTemplateArtwork(template);
                            return (
                              <li key={template.id}>
                                <StarterCard
                                  ref={(node) => {
                                    if (node) cardRefs.current.set(template.id, node);
                                    else cardRefs.current.delete(template.id);
                                  }}
                                  title={template.name}
                                  description={
                                    cardView === 'hidden'
                                      ? 'Restore this workflow to use it again.'
                                      : (template.description ?? 'Start a guided project.')
                                  }
                                  illustration={Illustration}
                                  onClick={() =>
                                    cardView === 'hidden'
                                      ? void unhideTemplate(template)
                                      : openCardPreview(template.id)
                                  }
                                  className="h-full min-h-[152px]"
                                />
                              </li>
                            );
                          })}
                      </ul>
                      {orderedCardTemplates.length > 6 && (
                        <div className="mt-1.5">
                          <Button
                            variant="link"
                            className="h-auto p-0 text-detail font-normal text-foreground/50 hover:text-foreground/70"
                            onClick={() => setShowAllCards((current) => !current)}
                            aria-expanded={showAllCards}
                          >
                            {showAllCards ? 'Show fewer' : 'See all workflows →'}
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
        <NewWorkflowDialog open={newWorkflowOpen} onOpenChange={setNewWorkflowOpen} />
        <NewWorkflowDialog
          open={editTemplate !== null}
          onOpenChange={(next) => !next && setEditTemplate(null)}
          editTemplate={editTemplate}
        />
      </>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          size="wide"
          className="flex h-[80vh] max-h-[720px] flex-col gap-0 overflow-hidden p-0"
        >
          <DialogHeader className="border-b border-border/40 px-5 py-3 text-left">
            <DialogTitle>{titleOverride ?? 'Workflow library'}</DialogTitle>
            {descriptionOverride ? (
              <DialogDescription className="text-detail text-muted-foreground">
                {descriptionOverride}
              </DialogDescription>
            ) : (
              <DialogDescription className="sr-only">
                Pick a guided workflow to start in this project.
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="grid min-h-0 flex-1 grid-cols-1 gap-0 md:grid-cols-[20rem_1fr]">
            {showLeftPane && (
              <div className="flex min-h-0 flex-col border-border/40 md:border-r">
                <div className="min-h-0 flex-1 overflow-y-auto pt-2">
                  {isLoading && (
                    <div className="px-5">
                      {[0, 1, 2].map((i) => (
                        <div key={i} className="flex items-start gap-3 py-3">
                          <Skeleton className="size-4 shrink-0 rounded-md mt-0.5" />
                          <div className="flex-1 space-y-1.5">
                            <Skeleton className="h-[12px] w-3/4" />
                            <Skeleton className="h-[11px] w-2/5" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {isError && (
                    <Empty className="border-0">
                      <EmptyHeader>
                        <EmptyTitle>Couldn&apos;t load workflows</EmptyTitle>
                        <EmptyDescription>
                          We hit a snag loading your workflows — try again.
                        </EmptyDescription>
                      </EmptyHeader>
                    </Empty>
                  )}

                  {!isLoading &&
                    !isError &&
                    filteredTemplates.length === 0 &&
                    templates.length === 0 &&
                    (inputKindFilter === 'article' ? (
                      <Empty className="border-0">
                        <EmptyHeader>
                          <EmptyTitle>No article workflows yet</EmptyTitle>
                          <EmptyDescription>
                            More are coming. For now, drop this article into a fresh chat to draft
                            posts.
                          </EmptyDescription>
                        </EmptyHeader>
                        {onEmptyFallback && (
                          <div className="mt-4 flex justify-center">
                            <Button
                              type="button"
                              variant="flat"
                              onClick={() => {
                                onEmptyFallback();
                                onOpenChange(false);
                              }}
                            >
                              Open article in chat
                            </Button>
                          </div>
                        )}
                      </Empty>
                    ) : (
                      <Empty className="border-0">
                        <EmptyHeader>
                          <EmptyTitle>No workflows yet</EmptyTitle>
                          <EmptyDescription>More workflows are on the way.</EmptyDescription>
                        </EmptyHeader>
                      </Empty>
                    ))}

                  {!isLoading && filteredTemplates.length > 0 && (
                    <ul>
                      {filteredTemplates.map((template) => (
                        <li key={template.id}>
                          <WorkflowTemplateRow
                            template={template}
                            selected={template.id === selectedId}
                            onSelect={handleSelect}
                          />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            {showRightPane && (
              <div className="flex min-h-0 flex-col">
                {isMobile && mobileView === 'preview' && (
                  <button
                    type="button"
                    onClick={() => setMobileView('list')}
                    className="flex items-center gap-1.5 px-5 pt-3 text-detail text-muted-foreground hover:text-foreground"
                  >
                    <ArrowLeftIcon className="size-3" weight="regular" />
                    All workflows
                  </button>
                )}
                <WorkflowTemplatePreview
                  template={selectedTemplate}
                  onUse={(id) => handleAssignClick(id)}
                  isAssigning={assignMutation.isPending}
                  useLabel={useLabel}
                  onAttach={
                    onAttach
                      ? (id) => {
                          const t = templates.find((tt) => tt.id === id);
                          if (t) {
                            onAttach(t);
                            onOpenChange(false);
                          }
                        }
                      : undefined
                  }
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={reassignTemplateId !== null}
        onOpenChange={(next) => !next && setReassignTemplateId(null)}
      >
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Replace running workflow?</DialogTitle>
            <DialogDescription>
              This project already has a workflow running. Picking a new one will replace its
              current step and history.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="flat"
              onClick={() => setReassignTemplateId(null)}
              disabled={assignMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="neutral"
              onClick={confirmReassign}
              disabled={assignMutation.isPending}
            >
              Replace workflow
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

WorkflowLibraryDialog.displayName = 'WorkflowLibraryDialog';

export { dispatchOpenSettings };
